/**
 * What someone without Smart Stack + sees instead of a prompt.
 *
 * Two versions, because they need different next steps. A signed-out visitor
 * may well already be a member — the library is linked from the header, which
 * members see too before they sign in — so signing in comes first and joining
 * second. A signed-in free account is definitely not a member, so joining is
 * the one thing that helps.
 *
 * The same card on the library index and on a single prompt's page, so the
 * offer is worded once.
 */

import { ExternalLink, Lock } from "lucide-react";
import { useLocation } from "wouter";
import { BUTTON } from "@/lib/buttonStyles";
import { MEMBERSHIP } from "@/lib/ecosystemData";
import { useAuthModal } from "@/contexts/AuthModalContext";

export default function PromptsUpgradeNotice({
  access,
  promptTitle,
}: {
  access: "signed-out" | "upgrade";
  /** Named in the heading on a single prompt's page. */
  promptTitle?: string;
}) {
  const { open } = useAuthModal();
  const [location] = useLocation();

  return (
    <div className="rounded-xl border border-border border-l-4 border-l-primary bg-card p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 mt-1 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-['Fraunces'] text-xl sm:text-2xl font-semibold text-foreground leading-snug">
            {promptTitle
              ? `"${promptTitle}" is part of ${MEMBERSHIP.name}`
              : `The Prompt Library is part of ${MEMBERSHIP.name}`}
          </p>
          <p className="mt-2 text-base text-foreground/75 leading-relaxed">
            {access === "signed-out"
              ? `Already a member? Log in to open every prompt. New here? Join ${MEMBERSHIP.name} to fill in each prompt and copy it into ChatGPT, Claude or Gemini.`
              : `Your free account can browse the library. Join ${MEMBERSHIP.name} to fill in each prompt and copy it into ChatGPT, Claude or Gemini.`}
          </p>

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
            {access === "signed-out" ? (
              <button
                type="button"
                onClick={() =>
                  open({
                    mode: "login",
                    returnTo: location,
                    title: "Log in to open the Prompt Library",
                    description: `Use the email you joined ${MEMBERSHIP.name} with.`,
                  })
                }
                className={BUTTON.primary}
              >
                Log in
              </button>
            ) : null}
            <a
              href={MEMBERSHIP.checkoutHref}
              target="_blank"
              rel="noopener noreferrer"
              className={access === "signed-out" ? BUTTON.secondary : BUTTON.primary}
            >
              Join {MEMBERSHIP.name}
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
            <p className="text-base text-muted-foreground">Only {MEMBERSHIP.price}.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
